import React, { FC, ReactNode, useEffect, useState } from 'react';
import { ColumnProps, PaginationConfig } from 'antd/lib/table';
import PageWithHeader from '@app/components/PageWithHeader';
import PageHeader from '@app/components/header/PageHeader';
import TableOfCards from '@app/components/tables/TableOfCards';
import { toAntdPaginationTotal } from '@app/components/tables/paginationTotal';
import { TableImportantText, TableStandardText } from '@app/components/tables/Table';
import InputBox from '@app/components/inputs/inputBox/InputBox';
import NakedFormInput from '@app/components/inputs/NakedFormInput';
import useLoadable from '@app/hooks/loadable/useLoadable';
import useLastResolvedTotal from '@app/hooks/loadable/useLastResolvedTotal';
import useInfraStores from '@app/hooks/useInfraStores';
import { useDebounce } from '@app/hooks/useDebounce';
import Loadable from '@app/utils/Loadable';
import browserHistory from '@app/utils/browserHistory';
import { OrganizationServerResponse } from '@app/domain/userManagement/organizations';
import { searchOrganizations } from '@app/services/userManagement/organizationsSearchService';
import MorteeMode from '@mortee/morteeMode';
import validationSystemServices from '@mortee/services/validationSystemServices';
import { ValidationSystemOrganizationServerResponse } from '@mortee/domain/validationSystem';

const PAGE_SIZE_OPTIONS = ['10', '25', '50', '100'];

interface OrganizationsTableState {
  page: number;
  pageSize: number;
}

const OrganizationSetupListPage: FC = () => {
  const { navigationStore } = useInfraStores<MorteeMode>();
  const [searchTerm, setSearchTerm] = useState<string>('');
  const debouncedSearchTerm = useDebounce(searchTerm.trim());
  const [tableState, setTableState] = useState<OrganizationsTableState>({ page: 1, pageSize: 25 });

  useEffect(() => {
    setTableState((currentTableState) => (currentTableState.page === 1 ? currentTableState : { ...currentTableState, page: 1 }));
  }, [debouncedSearchTerm]);

  const [organizationsPage] = useLoadable(
    (abortSignal) =>
      searchOrganizations(
        {
          query: debouncedSearchTerm || undefined,
          sort: 'Name',
          order: 'ASC',
          offset: (tableState.page - 1) * tableState.pageSize,
          limit: tableState.pageSize,
        },
        abortSignal,
      ),
    [debouncedSearchTerm, tableState],
  );

  const total = useLastResolvedTotal(organizationsPage);

  // Only organizations that use supplier validation come back, with their portal's subdomain and reference id
  const [listedOrganizations] = useLoadable(() => validationSystemServices.getAllValidationSystemOrganizations(), []);
  const listedOrganizationsById: Loadable<Map<string, ValidationSystemOrganizationServerResponse>> = listedOrganizations.map(
    (organizations) => new Map(organizations.map((organization) => [organization.id, organization])),
  );

  const paginationConfig: PaginationConfig = {
    current: tableState.page,
    pageSize: tableState.pageSize,
    total: toAntdPaginationTotal(total),
    showSizeChanger: true,
    pageSizeOptions: PAGE_SIZE_OPTIONS,
    hideOnSinglePage: false,
  };

  return (
    <PageWithHeader width='full' header={<PageHeader>Organization Setup</PageHeader>}>
      <InputBox appearance='line' colorScheme='secondary'>
        <NakedFormInput
          name='organization-setup-list-search'
          dataTestId='organization-setup-list-search'
          type='text'
          colorScheme='secondary'
          placeholderStyle='onlyWhenEmpty'
          heightType='thick'
          clearable
          placeholder='🔍 Search for Id / Name'
          value={searchTerm}
          onChange={(newValue): void => setSearchTerm(newValue ?? '')}
          autoFocus
        />
      </InputBox>
      <TableOfCards
        id='organization-setup-list-table'
        dataTestId='organization-setup-list-table'
        columns={createColumns(listedOrganizationsById)}
        dataSource={organizationsPage.result?.content ?? []}
        loading={!organizationsPage.isResolved()}
        pagination={paginationConfig}
        onChange={(newPagination: PaginationConfig): void => {
          const pageSize = newPagination.pageSize ?? tableState.pageSize;
          setTableState({ page: pageSize === tableState.pageSize ? (newPagination.current ?? 1) : 1, pageSize });
        }}
        onRow={(organization: OrganizationServerResponse): { onClick: React.MouseEventHandler<HTMLDivElement> } => ({
          onClick: (event): void => {
            event.preventDefault();
            event.stopPropagation();
            browserHistory.push(navigationStore.generateOrganizationSetupLink(organization.id));
          },
        })}
        rowClickable
        rowKey={(organization: OrganizationServerResponse): string => organization.id}
      />
    </PageWithHeader>
  );
};

export default OrganizationSetupListPage;

function createColumns(
  listedOrganizationsById: Loadable<Map<string, ValidationSystemOrganizationServerResponse>>,
): ColumnProps<OrganizationServerResponse>[] {
  const renderListedInfo = (
    organizationId: string,
    toText: (listedOrganization: ValidationSystemOrganizationServerResponse | undefined) => string,
  ): ReactNode => {
    if (!listedOrganizationsById.isResolved()) {
      return <TableStandardText>{listedOrganizationsById.isRejected() ? '?' : '…'}</TableStandardText>;
    }

    return <TableStandardText>{toText(listedOrganizationsById.result.get(organizationId))}</TableStandardText>;
  };

  return [
    {
      title: 'Organization',
      key: 'name',
      render: (_text: unknown, organization: OrganizationServerResponse): ReactNode => (
        <TableImportantText data-testid='organization-setup-list-name'>{organization.name}</TableImportantText>
      ),
    },
    {
      title: 'Uses Supplier Validation',
      key: 'usesSupplierValidation',
      render: (_text: unknown, organization: OrganizationServerResponse): ReactNode =>
        renderListedInfo(organization.id, (listedOrganization) => (listedOrganization ? 'Yes' : '—')),
    },
    {
      title: 'Registration Portal',
      key: 'portal',
      render: (_text: unknown, organization: OrganizationServerResponse): ReactNode =>
        renderListedInfo(
          organization.id,
          (listedOrganization) => listedOrganization?.subdomain ?? listedOrganization?.ref ?? '—',
        ),
    },
  ];
}
